import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    IconButton,
    Tooltip,
    Skeleton,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { getFactorRatings } from "../../../api/stocks_api";
import { getRecentlyViewed } from "../../../api/api";
import RecentlyViewedWidget from "../Widgets/recentlyViewedBox";
import { Info } from "lucide-react";
import { fmtL } from "../../../utils/currency";

/* -------------------- GRADE COLOR MAP -------------------- */
const gradeBackground = (grade) => {
    const map = {
        "A+": "var(--color-grade-a-plus)",
        "A": "var(--color-grade-a)",
        "A-": "var(--color-grade-a-minus)",

        "B+": "var(--color-grade-b-plus)",
        "B": "var(--color-grade-b)",
        "B-": "var(--color-grade-b-minus)",

        "C+": "var(--color-grade-c-plus)",
        "C": "var(--color-grade-c)",
        "C-": "var(--color-grade-c-minus)",

        "D+": "var(--color-grade-d-plus)",
        "D": "var(--color-grade-d)",
        "D-": "var(--color-grade-d-minus)",

        "E": "var(--color-grade-e)",

        "F": "var(--color-grade-f)",
    };

    return map[grade] ?? "var(--color-grade-unknown)";
};

/* -------------------- TABLE SKELETON -------------------- */
const TableSkeleton = () => (
    <Box sx={{ p: 3 }}>
        {[...Array(5)].map((_, idx) => (
            <Box key={idx} sx={{
                display: "flex",
                gap: 2,
                mb: 2,
                pb: 2,
                borderBottom: idx < 4 ? "1px solid var(--color-border-primary)" : "none",
            }}>
                <Skeleton variant="text" width="40%" height={32} sx={{
                    bgcolor: "var(--color-surface-hover)",
                    "&::after": { background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)" },
                }} />
                <Skeleton variant="rounded" width={80} height={32} sx={{
                    bgcolor: "var(--color-surface-hover)",
                    "&::after": { background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)" },
                }} />
            </Box>
        ))}
    </Box>
);

const Ratings = ({ stock }) => {
    const { ticker } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [factorRatings, setFactorRatings] = useState(null);
    const [recentlyViewed, setRecentlyViewed] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(true);

    useEffect(() => {
        if (!ticker) return;

        const loadFactorRatings = async () => {
            setLoading(true);
            try {
                const res = await getFactorRatings(ticker, "overall");

                if (res?.authExpired) {
                    navigate("/login");
                    return;
                }

                if (res?.success && res?.response_data && res.response_data.overall_grade != null) {
                    setFactorRatings(res.response_data);
                } else {
                    setFactorRatings(null);
                }
            } catch (err) {
                console.error("FA Ratings fetch error:", err);
                setFactorRatings(null);
            }
            setLoading(false);
        };

        loadFactorRatings();
    }, [ticker, navigate]);

    /* -------------------- RECENTLY VIEWED -------------------- */
    useEffect(() => {
        const fetchRecentlyViewed = async () => {
            setLoadingRecent(true);
            const res = await getRecentlyViewed();

            if (res.success) {
                setRecentlyViewed(
                    res.data.filter((item) => item.ticker !== stock?.ticker)
                );
            }
            setLoadingRecent(false);
        };

        if (stock?.ticker) fetchRecentlyViewed();
    }, [stock?.ticker]);

    /* -------------------- LOADING STATE -------------------- */
    if (loading) {
        return (
            <Box sx={{ display: "flex", gap: 3, width: "100%", px: { xs: 2, md: 3 }, py: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Paper elevation={0} sx={{
                        bgcolor: "var(--color-surface)",
                        border: "1px solid var(--color-border-primary)",
                        borderRadius: "var(--radius-xl)",
                        overflow: "hidden",
                        boxShadow: "var(--shadow-md)",
                    }}>
                        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 2, borderBottom: "1px solid var(--color-border-primary)" }}>
                            <Skeleton variant="text" width={200} height={32} sx={{ mb: 1, bgcolor: "var(--color-surface-hover)" }} />
                            <Skeleton variant="text" width="60%" height={20} sx={{ bgcolor: "var(--color-surface-hover)" }} />
                        </Box>
                        <TableSkeleton />
                    </Paper>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
                    <RecentlyViewedWidget data={[]} loading={true} />
                </Box>
            </Box>
        );
    }

    if (!factorRatings) {
        return (
            <Box sx={{ display: "flex", gap: 3, width: "100%", px: { xs: 2, md: 3 }, py: 2, flexDirection: { xs: "column", lg: "row" } }}>
                <Box sx={{ flex: 1 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            bgcolor: "var(--color-surface)",
                            border: "1px solid var(--color-border-primary)",
                            borderRadius: "var(--radius-xl)",
                            overflow: "hidden",
                            boxShadow: "var(--shadow-md)",
                            p: { xs: 3, sm: 4 },
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 200,
                            gap: 2,
                        }}
                    >
                        <Info size={40} style={{ color: "var(--color-text-muted)" }} />
                        <Typography sx={{ fontSize: "var(--font-size-lg)", fontWeight: 600, color: "var(--color-text-primary)" }}>
                            Data not available for this ticker
                        </Typography>
                        <Typography sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", textAlign: "center", maxWidth: 400 }}>
                            FA Ratings data is not available for {ticker}. This stock may not have sufficient data for factor analysis.
                        </Typography>
                    </Paper>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
                    <RecentlyViewedWidget data={recentlyViewed} loading={loadingRecent} />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", gap: 3, width: "100%", px: { xs: 2, md: 3 }, py: 2 }}>
            <Box sx={{ flex: 1 }}>
                <Paper elevation={0} sx={{
                    bgcolor: "var(--color-surface)",
                    border: "1px solid var(--color-border-primary)",
                    borderRadius: "var(--radius-xl)",
                    overflow: "hidden",
                }}>

                    {/* ── HEADER — mirrors Profitability.jsx layout ── */}
                    <Box sx={{
                        px: 3,
                        py: 3,
                        borderBottom: "1px solid var(--color-border-primary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}>
                        {/* Left: Title + Rating Date */}
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "1.3rem",
                                    fontWeight: 600,
                                    color: "var(--color-text-primary)",
                                }}
                            >
                                {ticker} FA Ratings
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "0.9rem",
                                    color: "var(--color-text-secondary)",
                                    mt: 0.5,
                                }}
                            >
                                FA Rating: {factorRatings?.overall_rating}
                            </Typography>
                            {/* <Typography sx={{ typography: "subtitle", color: "var(--color-text-muted)", mt: 0.5 }}>
                                Rating Date: {factorRatings?.rating_date?.split("T")[0]}
                            </Typography> */}
                        </Box>

                        {/* Right: Overall Grade chip + Overall Score */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box sx={{
                                display: "inline-flex",
                                px: 3,
                                py: 1,
                                borderRadius: "var(--radius-md)",
                                bgcolor: gradeBackground(factorRatings?.overall_grade),
                            }}>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        color: "#ffffff",
                                    }}
                                >
                                    {factorRatings?.overall_grade ?? "—"}
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    color: "var(--color-primary)",
                                }}>
                                FA Score: {factorRatings?.overall_score != null ? fmtL(factorRatings.overall_score, 1) : "—"}
                            </Typography>
                        </Box>
                    </Box>

                    {/* ── FACTOR TABLE ── */}
                    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                        <Box component="thead">
                            <Box component="tr">
                                {["Factor", "FA Grade"].map((header) => (
                                    <Box key={header} component="th" sx={{
                                        textAlign: "left",
                                        p: 2,
                                        typography: "tableHeader",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        color: "var(--color-text-muted)",
                                        borderBottom: "1px solid var(--color-border-primary)",
                                    }}>
                                        {header}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box component="tbody">
                            {[
                                { label: "Valuation", grade: factorRatings?.valuation_grade },
                                { label: "Growth", grade: factorRatings?.growth_grade },
                                { label: "Momentum", grade: factorRatings?.momentum_grade },
                                { label: "Financial Profitability", grade: factorRatings?.financial_profit_grade },
                                { label: "Risk", grade: factorRatings?.risk_grade },
                            ].map((row) => (
                                <Box component="tr" key={row.label} sx={{
                                    "&:hover": { bgcolor: "var(--color-surface-hover)" },
                                    transition: "background-color var(--transition-fast)",
                                }}>
                                    {/* Factor name */}
                                    <Box component="td" sx={{
                                        p: 2,
                                        borderBottom: "1px solid var(--color-border-primary)",
                                        typography: "paragraph",
                                        fontWeight: "var(--font-weight-medium)",
                                        color: "var(--color-text-primary)",
                                    }}>
                                        {row.label}
                                    </Box>

                                    {/* Grade chip */}
                                    <Box component="td" sx={{
                                        p: 2,
                                        borderBottom: "1px solid var(--color-border-primary)",
                                    }}>
                                        <Box sx={{
                                            display: "inline-flex",
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: "var(--radius-sm)",
                                            bgcolor: gradeBackground(row.grade),
                                        }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    color: "#ffffff",
                                                }}
                                            >
                                                {row.grade ?? "—"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}

                            {/* ── OVERALL ROW ── */}
                            {/* <Box component="tr" sx={{ bgcolor: "var(--color-bg-tertiary)" }}>
                                <Box component="td" sx={{
                                    p: 2,
                                    typography: "paragraph",
                                    fontWeight: "var(--font-weight-bold)",
                                    color: "var(--color-text-primary)",
                                    borderTop: "2px solid var(--color-border-secondary)",
                                }}>
                                    Overall
                                </Box>
                                <Box component="td" sx={{
                                    p: 2,
                                    borderTop: "2px solid var(--color-border-secondary)",
                                }}>
                                    <Box sx={{
                                        display: "inline-flex",
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: "var(--radius-sm)",
                                        bgcolor: gradeBackground(factorRatings?.overall_grade),
                                    }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                color: "#ffffff",
                                            }}
                                        >
                                            {factorRatings?.overall_grade ?? "—"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box> */}
                        </Box>
                    </Box>
                </Paper>
            </Box>

            {/* ── RECENTLY VIEWED ── */}
            <Box sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
                <RecentlyViewedWidget data={recentlyViewed} loading={loadingRecent} />
            </Box>
        </Box>
    );
};

export default Ratings;